import type { WistiaPlayerElement } from '@wistia/wistia-player-react';
import { WistiaPlayer } from '@wistia/wistia-player-react';
import type { ApolloQueryResult } from '@apollo/client';
import styled from 'styled-components';
import { mq } from '@wistia/vhs';
import type { Dispatch, SetStateAction, JSX } from 'react';
import { useCallback, useRef } from 'react';
import { isEmptyString, isNil } from '~/utilities/type-guards';
import { trackLanguageChangesWithSnowplow } from '~/singlePageApp/utilities/trackLanguageChangesWithSnowplow';
import { fastBaseHostname } from '~/constantDefinitions';
import type { MediaPageCustomizations } from '~/singlePageApp/screens/MediaPreview/customize/MediaPageCustomizationsContext';
import { MemoizedDangerouslyRenderedHtml } from '~/components/MemoizedDangerouslyRendered';
import { LetterBox, LetterBoxLightener, StuffBelowMediaContainer } from './Shared';
import { TabbedArea } from '../TabbedArea';
import type { AnonymousMediaQuery } from '../__gql/AnonymousMedia.gql.gen';
import type { MediaPreviewQuery } from '../../MediaPreview/__gql/MediaPreview.gql.gen';

const AUDIO_HEIGHT_TINY = 48;
const AUDIO_HEIGHT_BIG = 218;
const MAX_WIDTH_FOR_CONTENT_BELOW_MEDIA = 1100;

const AudioPlayerWrapper = styled.div`
  height: ${AUDIO_HEIGHT_TINY}px;
  margin: 0 auto;
  max-width: ${MAX_WIDTH_FOR_CONTENT_BELOW_MEDIA}px;
  width: 100%;

  ${mq.smAndUp} {
    height: ${AUDIO_HEIGHT_BIG}px;
  }
`;

type AudioLayoutProps = {
  commentingDisabled: boolean;
  customizations?: MediaPageCustomizations;
  doNotTrack: boolean;
  hashedId: string;
  media:
    | NonNullable<AnonymousMediaQuery['anonymousMedia']>
    | NonNullable<MediaPreviewQuery['media']>;
  testId: string;
  videoEmbed: WistiaPlayerElement | null;
  setVideoEmbed: Dispatch<SetStateAction<WistiaPlayerElement | null>>;
  fetchMore: (arg: GenericRecord) => Promise<ApolloQueryResult<AnonymousMediaQuery>>;
  shouldShowTranscriptTab: boolean;
};

export const AudioLayout = ({
  commentingDisabled,
  customizations,
  hashedId,
  media,
  doNotTrack,
  testId,
  shouldShowTranscriptTab,
  videoEmbed,
  setVideoEmbed,
  fetchMore,
}: AudioLayoutProps): JSX.Element => {
  const {
    topLevelAudienceComments,
    topLevelTeamComments,
    id: mediaId,
    displayDescription,
    playerColor,
    publicCommentsSelection: publicCommentsSelectionFromMedia,
  } = media;
  const {
    shouldShowDescription = true,
    fontFamily = 'inherit',
    publicCommentsSelection: publicCommentsSelectionFromCustomizations,
  } = customizations ?? {};
  const hasLetterboxColor =
    customizations?.letterboxColor != null && !isEmptyString(customizations.letterboxColor);

  const audioPlayerRef = useRef<WistiaPlayerElement | null>(null);

  const publicCommentsSelection = isNil(publicCommentsSelectionFromCustomizations)
    ? publicCommentsSelectionFromMedia
    : publicCommentsSelectionFromCustomizations;

  const onLoadedMetadata = useCallback((): void => {
    const player = audioPlayerRef.current;
    if (player) {
      setVideoEmbed(player);
      trackLanguageChangesWithSnowplow(player, 'anonymous_media_page');
    }
  }, [setVideoEmbed]);

  return (
    <>
      <LetterBox color={hasLetterboxColor ? customizations.letterboxColor : playerColor}>
        {!hasLetterboxColor && <LetterBoxLightener />}
        <AudioPlayerWrapper data-testid={testId}>
          <WistiaPlayer
            style={{ height: '100%', width: '100%' }}
            doNotTrack={doNotTrack}
            embedHost={fastBaseHostname}
            mediaId={hashedId}
            onLoadedMetadata={onLoadedMetadata}
            ref={audioPlayerRef}
            swatch={false}
          />
        </AudioPlayerWrapper>
      </LetterBox>
      <StuffBelowMediaContainer style={{ fontFamily }}>
        {displayDescription != null && shouldShowDescription ? (
          <MemoizedDangerouslyRenderedHtml html={displayDescription} />
        ) : null}

        <TabbedArea
          audienceComments={topLevelAudienceComments}
          commentingDisabled={commentingDisabled}
          teamComments={topLevelTeamComments}
          mediaId={mediaId}
          mediaHashedId={hashedId}
          shouldShowTranscript={shouldShowTranscriptTab}
          videoEmbed={videoEmbed}
          publicCommentsSelection={publicCommentsSelection}
          fetchMore={fetchMore}
          fontFamily={fontFamily}
        />
      </StuffBelowMediaContainer>
    </>
  );
};
